import {
  Button,
  Icon,
  Stack,
  Tooltip,
  Spinner
} from '@efast_public/fjd-component-library';
import type { ButtonProps } from '@efast_public/fjd-component-library';
import { AntragObj } from '../../../api/models/Antrag/AntragsModel';
import { CaseDetailsModel } from '../../../api/models/Case/CaseDetailsModel';
import useAssignment from '../../../shared/hooks/useAssignment';
import { Bearbeitungsstand } from '../../../api/models/Status/Bearbeitungsstand';
import { useProfile } from '../../../shared/hooks/AuthContext';

interface ZuweisungSachbearbeiterProps {
  readonly antragObj: AntragObj | CaseDetailsModel;
  readonly isSchreibtisch: boolean;
  readonly buttonSize?: ButtonProps['size'];
}

function ZuweisungSachbearbeiter(
  props: ZuweisungSachbearbeiterProps
): JSX.Element {
  const { profile } = useProfile();
  const { assignCase, revokeCase, isPending } = useAssignment({
    antragObj: props.antragObj
  });
  const angemeldeterUserIstBearbeiter =
    props.antragObj.bearbeiterDto?.id === profile?.id;
  const caseAlreadyEdited =
    props.antragObj.firstInteractionTimestamp !== null &&
    props.antragObj.firstInteractionTimestamp !== undefined;
  const id = props.isSchreibtisch
    ? `zuweisungsButton_${props.antragObj.caseId}`
    : `bearbeiterCol_${props.antragObj.caseId}`;
  const idContainer = props.isSchreibtisch
    ? `zuweisungsButtonContainer_${props.antragObj.caseId}`
    : `bearbeiterColContainer_${props.antragObj.caseId}`;

  const buttonSize = props.buttonSize ?? 'sm';
  const currentBearbeiter: string | undefined =
    props.antragObj.bearbeiterDto?.fullName;
  const variant: ButtonProps['variant'] = props.isSchreibtisch
    ? 'secondary-outline'
    : 'primary';
  const revokeAssignment = async () => {
    await revokeCase(props.antragObj.version);
  };

  const assignCaseToBearbeiter = async () => {
    const id = profile?.id ? profile.id : '';
    await assignCase(id, props.antragObj.version);
  };

  if (isPending) {
    return <Spinner />;
  }
  // noch keinem zugewiesen
  if (
    !currentBearbeiter &&
    props.antragObj.bearbeitungsstand !== Bearbeitungsstand.ABGESCHLOSSEN
  ) {
    return (
      <Button
        id={id}
        onClick={assignCaseToBearbeiter}
        variant={variant}
        beforeIcon={'user-avatar-filled-alt'}
        size={buttonSize}
        aria-label={'mir zuweisen'}
      >
        Mir zuweisen
      </Button>
    );
  }
  // mir zugewiesen aber noch nicht bearbeitet
  if (
    angemeldeterUserIstBearbeiter &&
    !caseAlreadyEdited &&
    currentBearbeiter
  ) {
    return (
      <Button
        onClick={revokeAssignment}
        id={id}
        variant={'secondary-outline'}
        afterIcon={'close-outline'}
        size={buttonSize}
      >
        {currentBearbeiter}
      </Button>
    );
  }
  // mir zugewiesen aber schon bearbeitet
  if (angemeldeterUserIstBearbeiter && caseAlreadyEdited && currentBearbeiter) {
    return (
      <Stack
        direction={'row'}
        id={idContainer}
        spacing="xs"
        alignItems="center"
      >
        <Button
          id={id}
          disabled={true}
          variant={'secondary-outline'}
          size={buttonSize}
        >
          {currentBearbeiter}
        </Button>
        <Tooltip
          tooltip={
            'Dieser Antrag ist bereits zu weit bearbeitet, um die Zuweisung zurückzunehmen. Für Änderungen kontaktieren Sie bitte Ihre:n Vorgesetzte:n.'
          }
          placement="auto"
        >
          <div>
            <Icon name="information-outline" variant={'info'} />
          </div>
        </Tooltip>
      </Stack>
    );
  }

  // assigned to another user
  return props.isSchreibtisch ? (
    <Stack direction={'row'} id={idContainer}>
      <div id={id}>
        <b>Bearbeiter:in: </b>&nbsp; {currentBearbeiter}
      </div>
    </Stack>
  ) : (
    <span id={id}>{currentBearbeiter ?? 'Keine Bearbeiter:in'}</span>
  );
}

export default ZuweisungSachbearbeiter;
